<template>
  <div class="space-y-4">
    <div class="flex flex-col items-center">
      <div class="relative inline-block">
        <div
          :class="[
            'rounded-full overflow-hidden bg-gradient-to-br from-slate-700/85 to-slate-800 flex items-center justify-center border-4 border-cyan-400/35 shadow-lg shadow-black/20',
            sizeClasses.circle
          ]"
        >
          <img
            v-if="previewUrl || modelValue"
            :src="previewUrl || modelValue"
            :alt="displayName || placeholderAlt"
            class="w-full h-full object-cover"
          />
          <template v-else>
            <svg v-if="variant === 'logo'" :class="sizeClasses.icon" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                clip-rule="evenodd"
              />
            </svg>
            <svg v-else :class="sizeClasses.icon" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                clip-rule="evenodd"
              />
            </svg>
          </template>
        </div>

        <div class="relative">
          <button
            type="button"
            @click="showCameraMenu = !showCameraMenu"
            :class="[
              'absolute bottom-0 right-0 bg-[#2AD7DE] text-slate-950 rounded-full hover:bg-[#5de9f0] transition-all duration-200 transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-cyan-400/70 focus:ring-offset-2',
              sizeClasses.cameraBtn
            ]"
            :disabled="uploading"
            title="Opções de foto"
          >
            <svg :class="sizeClasses.cameraIcon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </button>

          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <div
              v-if="showCameraMenu"
              class="absolute right-0 mt-2 w-48 rounded-xl bg-[#435b7c]/95 backdrop-blur-sm border border-cyan-400/25 shadow-2xl shadow-black/45 py-2 z-50"
              @click="showCameraMenu = false"
            >
              <button
                v-if="modelValue"
                type="button"
                @click="viewPhoto"
                class="w-full text-left flex items-center px-4 py-2 text-sm text-slate-200 hover:bg-cyan-400/10 hover:text-cyan-200 transition"
              >
                <svg class="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
                <span>Ver Foto</span>
              </button>
              <button
                type="button"
                @click="triggerFileInput"
                class="w-full text-left flex items-center px-4 py-2 text-sm text-slate-200 hover:bg-cyan-400/10 hover:text-cyan-200 transition"
              >
                <svg class="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                </svg>
                <span>Nova Foto</span>
              </button>
              <div v-if="modelValue" class="border-t border-cyan-200/15 my-1"></div>
              <button
                v-if="modelValue"
                type="button"
                @click="deletePhoto"
                class="w-full text-left flex items-center px-4 py-2 text-sm text-red-300 hover:bg-red-500/25 transition"
              >
                <svg class="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
                <span>Excluir Foto</span>
              </button>
            </div>
          </transition>
        </div>

        <div
          v-if="uploading"
          class="absolute inset-0 rounded-full bg-black bg-opacity-40 flex items-center justify-center"
        >
          <div class="animate-spin rounded-full h-8 w-8 border-4 border-white border-t-[#2AD7DE]"></div>
        </div>
      </div>

      <input
        ref="fileInput"
        type="file"
        accept="image/*"
        class="hidden"
        @change="handleFileSelect"
        :disabled="uploading"
      />

      <p
        v-if="labelUnderPhoto"
        class="mt-4 text-lg font-semibold text-center"
        :class="labelUnderPhotoColorClass"
      >
        {{ labelUnderPhoto }}
      </p>
    </div>

    <div v-if="fileInfo" class="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-gray-700">
      <div class="flex items-start gap-2">
        <svg class="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path
            fill-rule="evenodd"
            d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zm-11-1a1 1 0 11-2 0 1 1 0 012 0z"
            clip-rule="evenodd"
          />
        </svg>
        <div>
          <p class="font-medium">{{ fileInfo.name }}</p>
          <p class="text-xs text-gray-600">{{ formatFileSize(fileInfo.size) }}</p>
        </div>
      </div>
    </div>

    <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
      <svg class="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path
          fill-rule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
          clip-rule="evenodd"
        />
      </svg>
      <div>
        <p class="font-medium text-red-900">{{ error }}</p>
      </div>
    </div>

    <div v-if="success" class="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
      <svg class="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path
          fill-rule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clip-rule="evenodd"
        />
      </svg>
      <p class="font-medium text-green-900">{{ success }}</p>
    </div>

    <div v-if="previewUrl" class="flex gap-3">
      <button
        type="button"
        @click="showCropModal = true"
        :disabled="uploading"
        class="flex-1 bg-[#2AD7DE] text-slate-950 font-medium py-2 px-4 rounded-lg hover:bg-[#5de9f0] transition disabled:opacity-50"
      >
        <i class="fas fa-crop-alt mr-2"></i>
        {{ uploading ? 'Ajustando...' : 'Ajustar Foto' }}
      </button>
      <button
        type="button"
        @click="confirmUpload"
        :disabled="uploading"
        class="flex-1 bg-green-600 text-white font-medium py-2 px-4 rounded-lg hover:bg-green-700 transition disabled:opacity-50"
      >
        <i class="fas fa-check mr-2"></i>
        {{ uploadButtonLabel }}
      </button>
    </div>

    <PhotoCropModal
      :visible="showCropModal"
      :preview-url="previewUrl"
      :name="cropModalName"
      @confirm="handleCropConfirm"
      @cancel="showCropModal = false"
    />

    <ImageLightbox
      :visible="showLightbox"
      :image-url="modelValue"
      :alt-text="lightboxAltText"
      @close="showLightbox = false"
    />
  </div>
</template>

<script>
import PhotoCropModal from './PhotoCropModal.vue'
import ImageLightbox from './ImageLightbox.vue'
import { useApi } from '../composables/useApi.js'

export default {
  name: 'ImageCropUpload',
  components: {
    PhotoCropModal,
    ImageLightbox
  },
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    /** Exibido no crop / acessibilidade (perfil, agente). */
    name: {
      type: String,
      default: ''
    },
    /** Nome da empresa (variant logo). */
    companyName: {
      type: String,
      default: ''
    },
    /** `avatar` (pessoa) ou `logo` (empresa) — ícone placeholder e limite padrão de tamanho. */
    variant: {
      type: String,
      default: 'avatar',
      validator: (v) => ['avatar', 'logo'].includes(v)
    },
    /**
     * `user_me` — PATCH FormData `/api/v1/me` (ou uploadEndpoint), atualiza localStorage.
     * `tenant_put` — PUT FormData tenant + logo.
     * `deferred` — só emite `photo-ready` / `photo-cleared` (ex.: agente, usuários CRUD).
     */
    uploadStrategy: {
      type: String,
      default: 'user_me',
      validator: (v) => ['user_me', 'tenant_put', 'deferred'].includes(v)
    },
    /** Atalho: equivale a upload-strategy="deferred". */
    deferred: {
      type: Boolean,
      default: false
    },
    tenantName: {
      type: String,
      default: ''
    },
    tenantPhone: {
      type: String,
      default: ''
    },
    uploadEndpoint: {
      type: String,
      default: '/api/v1/me'
    },
    tenantPutEndpoint: {
      type: String,
      default: '/api/v1/tenant'
    },
    fileFieldName: {
      type: String,
      default: 'user[profile_image]'
    },
    uploadFilename: {
      type: String,
      default: 'profile.jpg'
    },
    logoUploadFilename: {
      type: String,
      default: 'logo.jpg'
    },
    /** Classe (Tailwind) para o texto abaixo da foto (perfil/agent/crud). */
    labelUnderPhotoColorClass: {
      type: String,
      default: 'text-gray-900'
    },
    maxFileSize: {
      type: Number,
      default: null
    },
    lightboxAltSuffix: {
      type: String,
      default: 'Foto'
    },
    confirmDeleteMessage: {
      type: String,
      default: ''
    },
    /** `lg` — 32 (padrão perfil/tenant); `md` — 24 (ex.: modal usuários). */
    size: {
      type: String,
      default: 'lg',
      validator: (v) => ['lg', 'md'].includes(v)
    }
  },
  emits: ['update:modelValue', 'upload-success', 'upload-error', 'photo-ready', 'photo-cleared'],
  data() {
    return {
      previewUrl: null,
      fileInfo: null,
      uploading: false,
      error: '',
      success: '',
      selectedFile: null,
      showCropModal: false,
      showCameraMenu: false,
      showLightbox: false
    }
  },
  computed: {
    strategy() {
      return this.deferred ? 'deferred' : this.uploadStrategy
    },
    effectiveMaxBytes() {
      if (this.maxFileSize != null) return this.maxFileSize
      return this.variant === 'logo' ? 1024 * 1024 : 5 * 1024 * 1024
    },
    displayName() {
      if (this.variant === 'logo') return (this.companyName || this.name || '').trim()
      return (this.name || '').trim()
    },
    labelUnderPhoto() {
      if (this.variant === 'logo') return this.companyName || this.name || ''
      return this.name || ''
    },
    cropModalName() {
      return this.displayName || (this.variant === 'logo' ? 'Empresa' : 'Foto')
    },
    placeholderAlt() {
      return this.variant === 'logo' ? 'Logo' : 'Foto de perfil'
    },
    lightboxAltText() {
      const base = this.displayName || (this.variant === 'logo' ? 'Empresa' : 'Foto')
      return `${base} — ${this.lightboxAltSuffix}`
    },
    deleteConfirmText() {
      if (this.confirmDeleteMessage) return this.confirmDeleteMessage
      if (this.strategy === 'tenant_put') return 'Tem certeza que deseja excluir a foto da empresa?'
      if (this.strategy === 'deferred') return 'Remover esta imagem? (salve o formulário para aplicar.)'
      return 'Tem certeza que deseja excluir a foto de perfil?'
    },
    uploadButtonLabel() {
      if (this.strategy === 'deferred') {
        return this.uploading ? 'Usando...' : 'Usar esta foto'
      }
      if (this.strategy === 'tenant_put') {
        return this.uploading ? 'Enviando...' : 'Usar esta foto'
      }
      return this.uploading ? 'Usando...' : 'Usar esta foto'
    },
    sizeClasses() {
      if (this.size === 'md') {
        return {
          circle: 'w-24 h-24',
          icon: 'w-12 h-12 text-cyan-400/90',
          cameraBtn: 'p-2.5',
          cameraIcon: 'w-4 h-4'
        }
      }
      return {
        circle: 'w-32 h-32',
        icon: 'w-16 h-16 text-cyan-400/90',
        cameraBtn: 'p-3',
        cameraIcon: 'w-5 h-5'
      }
    }
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click()
    },
    handleFileSelect(event) {
      const file = event.target.files?.[0]
      if (file) this.processFile(file)
    },
    processFile(file) {
      this.error = ''
      this.success = ''

      if (!file.type.startsWith('image/')) {
        this.error = 'Por favor, selecione uma imagem válida.'
        return
      }

      if (file.size > this.effectiveMaxBytes) {
        this.error = `A imagem deve ter no máximo ${this.formatFileSize(this.effectiveMaxBytes)}.`
        return
      }

      this.selectedFile = file
      this.fileInfo = { name: file.name, size: file.size }

      const reader = new FileReader()
      reader.onload = (e) => {
        this.previewUrl = e.target.result
        this.$nextTick(() => {
          this.showCropModal = true
        })
      }
      reader.readAsDataURL(file)
    },
    handleCropConfirm(cropData) {
      this.previewUrl = cropData.croppedDataUrl
      this.selectedFile = null
      this.showCropModal = false
      if (this.strategy === 'tenant_put') {
        return
      }
      this.$emit('update:modelValue', cropData.croppedDataUrl)
    },
    async confirmUpload() {
      if (!this.previewUrl) return

      this.uploading = true
      this.error = ''
      this.success = ''

      try {
        const blob = await this.dataUrlToBlob(this.previewUrl)

        if (this.strategy === 'deferred') {
          this.$emit('photo-ready', { blob, filename: this.uploadFilename })
          this.$emit('update:modelValue', this.previewUrl)
          this.success = 'Foto pronta. Salve o formulário para enviar.'
          setTimeout(() => {
            this.success = ''
          }, 4000)
          return
        }

        if (this.strategy === 'tenant_put') {
          const { putFormData } = useApi()
          const formData = new FormData()
          formData.append('tenant[name]', this.tenantName || '')
          formData.append('tenant[phone]', this.tenantPhone || '')
          formData.append('tenant[logo]', blob, this.logoUploadFilename)
          const { data, error } = await putFormData(this.tenantPutEndpoint, formData)
          if (error) {
            this.error = 'Erro ao salvar foto da empresa.'
            this.$emit('upload-error', error)
            return
          }
          this.$emit('update:modelValue', data.logo_url || this.previewUrl)
          this.$emit('upload-success', { tenant: data })
          this.success = 'Foto atualizada com sucesso!'
          this.resetUpload()
          setTimeout(() => {
            this.success = ''
          }, 2000)
          return
        }

        const { upload } = useApi()
        const formData = new FormData()
        formData.append(this.fileFieldName, blob, this.uploadFilename)

        const { data, error } = await upload(this.uploadEndpoint, formData)

        if (error) {
          console.error('Erro ao salvar foto:', error)
          this.error =
            this.variant === 'logo' ? 'Erro ao salvar imagem.' : 'Erro ao salvar foto de perfil'
          this.$emit('upload-error', error)
          return
        }

        localStorage.setItem('user', JSON.stringify(data))
        this.$emit('update:modelValue', data.profile_image || this.previewUrl)
        this.$emit('upload-success', {
          url: data.profile_image,
          user: data
        })

        this.success = 'Foto atualizada com sucesso!'

        setTimeout(() => {
          this.resetUpload()
        }, 2000)
      } catch (err) {
        console.error('Erro ao fazer upload:', err)
        this.error = err.message || 'Erro ao enviar a foto.'
        this.$emit('upload-error', err)
      } finally {
        this.uploading = false
      }
    },
    resetUpload() {
      this.selectedFile = null
      this.fileInfo = null
      this.previewUrl = null
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },
    viewPhoto() {
      if (this.modelValue) this.showLightbox = true
    },
    async deletePhoto() {
      if (!confirm(this.deleteConfirmText)) return

      this.uploading = true
      this.error = ''
      this.success = ''

      try {
        if (this.strategy === 'deferred') {
          this.previewUrl = null
          this.fileInfo = null
          this.selectedFile = null
          if (this.$refs.fileInput) this.$refs.fileInput.value = ''
          this.$emit('update:modelValue', '')
          this.$emit('photo-cleared')
          this.success = 'Foto removida. Salve o formulário para aplicar.'
          setTimeout(() => {
            this.success = ''
          }, 3000)
          return
        }

        if (this.strategy === 'tenant_put') {
          const { put } = useApi()
          const { data, error } = await put(this.tenantPutEndpoint, {
            tenant: {
              name: this.tenantName || '',
              phone: this.tenantPhone || '',
              remove_logo: true
            }
          })
          if (error) {
            this.error = 'Erro ao excluir foto.'
            return
          }
          this.$emit('update:modelValue', '')
          this.$emit('upload-success', { tenant: data })
          this.success = 'Foto removida com sucesso!'
          this.previewUrl = null
          this.fileInfo = null
          setTimeout(() => {
            this.success = ''
          }, 3000)
          return
        }

        const { upload } = useApi()
        const formData = new FormData()
        formData.append('_method', 'DELETE')
        formData.append(this.fileFieldName, '')

        const { data, error } = await upload(this.uploadEndpoint, formData)

        if (error) {
          console.error('Erro ao deletar foto:', error)
          this.error = 'Erro ao deletar foto de perfil'
          return
        }

        localStorage.setItem('user', JSON.stringify(data))
        this.$emit('update:modelValue', '')
        this.$emit('upload-success', {
          url: null,
          user: data
        })

        this.success = 'Foto removida com sucesso!'
        this.previewUrl = null
        this.fileInfo = null

        setTimeout(() => {
          this.success = ''
        }, 3000)
      } catch (err) {
        console.error('Erro ao deletar foto:', err)
        this.error = 'Erro ao deletar foto de perfil'
      } finally {
        this.uploading = false
      }
    },
    async dataUrlToBlob(dataUrl) {
      const response = await fetch(dataUrl)
      return await response.blob()
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
    }
  }
}
</script>
